import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import styles from './TurnInBox.module.css';

/** Local-clock today as ISO YYYY-MM-DD (matches the rest of the live home). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Live Turn-In box for the 4–8 Home. Lists every configured subject with the
 * assignment due today (em-dash when nothing's due). Read-only; the subject
 * list and per-day assignments are managed in settings.
 */
export function TurnInBox({ classroomId }: { classroomId: string }) {
  const api = useApi();
  const iso = todayIso();

  const subjects = useQuery({
    queryKey: ['planner-subjects', classroomId],
    queryFn: () => api.planner.listSubjects(classroomId),
    enabled: !!classroomId,
  });
  const assignments = useQuery({
    queryKey: ['planner-assignments', classroomId, iso],
    queryFn: () => api.planner.listAssignments(classroomId, iso, iso),
    enabled: !!classroomId,
  });

  const bySubject = new Map((assignments.data ?? []).map((a) => [a.subjectId, a.assignment]));
  const subs = subjects.data ?? [];

  return (
    <section className={`glass glass--solid ${styles.box}`} data-testid="turn-in-box">
      <h2 className={styles.title}>Turn In</h2>
      {subs.length > 0 ? (
        <ul className={styles.list}>
          {subs.map((s) => {
            const assignment = (bySubject.get(s.id) ?? '').trim();
            return (
              <li key={s.id} className={styles.row}>
                <span className={styles.subject}>{s.name}</span>
                <span className={assignment ? styles.assignment : styles.assignmentEmpty}>
                  {assignment || '—'}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={styles.empty}>No subjects added yet.</p>
      )}
    </section>
  );
}
